import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { usePermissions, useUser } from '@tyro/api';
import { DialogCloseButton, FloatCardDraggableContainer } from '@tyro/core';
import { AddIcon, ArrowLeftIcon, StarSparklesIcon } from '@tyro/icons';
import { AnimatePresence, m } from 'motion/react';

interface PopoverHeaderProps {
  onClose: () => void;
  showBackButton?: boolean;
  onBack?: () => void;
  onNewConversation?: () => void;
  conversationTitle?: string | null;
  conversationId?: string | null;
}

const titleVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

const iconButtonSx = {
  width: 30,
  height: 30,
};

export function PopoverHeader({
  onClose,
  showBackButton,
  onBack,
  onNewConversation,
  conversationTitle,
  conversationId,
}: PopoverHeaderProps) {
  const { user } = useUser();
  const { isTyroUser } = usePermissions();
  const showConversationId =
    (isTyroUser || user?.isEmulatingAsTyroUser) &&
    showBackButton &&
    conversationId;

  return (
    <FloatCardDraggableContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: 1,
          minHeight: 52,
          borderBottom: 'solid 1px',
          borderBottomColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <AnimatePresence mode="wait" initial={false}>
            {showBackButton ? (
              <Stack
                key="conversation-title"
                component={m.div}
                direction="row"
                alignItems="center"
                spacing={0.5}
                variants={titleVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                sx={{ minWidth: 0 }}
              >
                <Tooltip title="Back to conversations">
                  <IconButton
                    onClick={onBack}
                    size="small"
                    aria-label="Back to conversations"
                    sx={iconButtonSx}
                  >
                    <ArrowLeftIcon />
                  </IconButton>
                </Tooltip>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    component="h2"
                    variant="subtitle1"
                    fontWeight={600}
                    noWrap
                  >
                    {conversationTitle || 'New conversation'}
                  </Typography>
                  {showConversationId && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {conversationId}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ) : (
              <Stack
                key="tyrone-title"
                component={m.div}
                direction="row"
                alignItems="center"
                spacing={0.5}
                variants={titleVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                sx={{ pl: 1 }}
              >
                <StarSparklesIcon sx={{ color: 'primary.main' }} />
                <Typography component="h2" variant="h6">
                  Tyrone
                </Typography>
              </Stack>
            )}
          </AnimatePresence>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Fade in={!!onNewConversation}>
            <Tooltip title="New conversation">
              <IconButton
                onClick={onNewConversation}
                size="small"
                aria-label="New conversation"
                sx={{
                  ...iconButtonSx,
                  visibility: onNewConversation ? 'visible' : 'hidden',
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Fade>
          <DialogCloseButton onClick={onClose} autoFocus={!showBackButton} />
        </Stack>
      </Stack>
    </FloatCardDraggableContainer>
  );
}
